/**
 * An HOC to pass content data to child components.
 *
 * Passes the content data to a wrapped component.
 * This HOC assumes that your component is using exact props.  Please update
 * your component to use exact props before using this HOC.
 *
 */
import * as React from "react";
import {Redirect, Route, Switch} from "react-router-dom";

import {Data, type Result} from "@khanacademy/wonder-blocks-data";
import {Errors, KAError} from "@repo/logging";
import {RequestInfo} from "@repo/shared-core";
import {parseUrl, toRelativeUrl, useLocation} from "@repo/urls";

import NotFound from "#app-shell-package/errors/not-found.tsx";
import type {Location, Match} from "#data-access/with-router.ts";

import {
    contentDataRequestHandler,
    getContentDataRequestId,
} from "./content-data-request-handler.ts";
import type {ContentData, ContentOptions} from "./types.ts";

/**
 * The ContentDataProvider is a component that watches changes in the route,
 * fetches data from the content API, and provides it to the children.
 *
 * This provider will work in concert with the SSR server to ensure that the
 * data can be pre-loaded and eventually pre-rendered.  This is a place
 * where we can do in-memory caching, local-storage caching, and even eager
 * loading if we decide it is a performance win.
 */

type Props<TData extends ContentData> = {
    children: (props: Result<TData>) => React.ReactNode;
};

type PreserveResultProps<TData extends ContentData> = {
    result: Result<TData>;
    context: string;
    children: (props: Result<TData>) => React.ReactNode;
};

// An unexpected redirect happens when the renderedPath is not the same
// as the requestedPath. The exception is for modals which have a valid
// redirect, so don't say it is unexpected if it is a modal.
function wasUnexpectedRedirect(data?: ContentData | null) {
    if (!data || !data.requestedPath || !data.renderedPath) {
        return false;
    }

    if (data.requestedPath !== data.renderedPath) {
        // modals are a bit tricky. The rendered path for the
        // modal is the course or unit it belongs to. If the rendered
        // path is different, but it is the prefix for the requested path
        // then we're OK.
        return !data.requestedPath.startsWith(data.renderedPath);
    }

    return false;
}

/**
 * This component will always render the previous result if the current result
 * is loading a different URL.
 */
class MaybePreserveLastResult<
    TData extends ContentData,
> extends React.Component<PreserveResultProps<TData>> {
    shouldComponentUpdate(nextProps: PreserveResultProps<TData>): boolean {
        const isLoading = ({status}: Result<TData>) =>
            status === "loading" || status === "no-data";
        /**
         * We want to preserve props while we load data for the new URL.
         */
        if (
            this.props.context === nextProps.context &&
            isLoading(nextProps.result)
        ) {
            return false;
        }

        return (
            this.props.context !== nextProps.context ||
            isLoading(this.props.result) !== isLoading(nextProps.result) ||
            this.props.children !== nextProps.children ||
            this.props.result !== nextProps.result
        );
    }

    render(): React.ReactNode {
        return this.props.children(this.props.result);
    }
}

const ContentDataProvider = <TData extends ContentData>(
    props: Props<TData>,
): React.ReactElement => {
    const lastContentOptions = React.useRef<ContentOptions | null | undefined>(
        null,
    );
    const location = useLocation();

    const _renderResult = (
        pathname: string,
        match: Match,
        result: Result<TData>,
    ): React.ReactNode => {
        if (result.status === "success") {
            const data = result.data;
            // Check if we need to redirect instead of trying to render the result
            if (data.redirectTo) {
                // Note: We pass query params as part of the GraphQL query
                // variables, and we are setting the `lang` param by default. We did
                // this to support to Legacy SSR; however, we are now fully migrated
                // for Goliath, so we can re-evaluate this logic.
                const url = parseUrl(data.redirectTo);

                // Remove the lang query param from the URL (only if we are trying
                // to redirect to a different locale).
                if (RequestInfo.kaLocale === url.searchParams.get("lang")) {
                    url.searchParams.delete("lang");
                }

                return <Redirect to={toRelativeUrl(url.href)} />;
            }

            if (wasUnexpectedRedirect(data)) {
                const renderedPath = data.renderedPath || "";

                const pathname = renderedPath.startsWith("/")
                    ? renderedPath
                    : "/" + renderedPath;

                const redirect = `${pathname}${location.search}`;

                return <Redirect to={redirect} push={false} />;
            }
        }

        // Context for preservation is easy.
        // If match contains a `kind` value then it's the path name split before
        // that, otherwise it's just the pathname.
        const context = match.params.kind
            ? pathname.split(`/${match.params.kind}/`)[0]
            : pathname;

        /**
         * For some cases, we want to preserve the data we have
         * while we load the data we want. This allows for some nice smooth UX
         * transitions around a tutorial page.
         *
         * We base this off the page URL, not the content URL.
         */
        return (
            <MaybePreserveLastResult
                context={context}
                result={result}
                children={props.children}
            />
        );
    };

    const _renderData = (
        pathname: string,
        match: Match,
        dataOptions: ContentOptions,
    ) => {
        lastContentOptions.current = dataOptions;

        return (
            <Data
                handler={() => contentDataRequestHandler(dataOptions)}
                requestId={getContentDataRequestId(dataOptions)}
            >
                {/* @ts-expect-error - TS2345 - Argument of type 'Result<ContentData>' is not assignable to parameter of type 'Result<TData>'. */}
                {(result) => _renderResult(pathname, match, result)}
            </Data>
        );
    };

    const _getDataOptions = (
        location: Location,
        reuseLastLocation:
            | ((params?: URLSearchParams) => boolean)
            | null
            | undefined,
        pathname: string,
    ): ContentOptions => {
        const {search} = location;
        const extraParams = new URLSearchParams(search);
        const reuse = reuseLastLocation?.(extraParams) ?? false;

        if (reuse && lastContentOptions.current != null) {
            return lastContentOptions.current;
        }

        if (reuse && lastContentOptions.current == null) {
            /**
             * Oh dear, we want to show the last request but we don't have it.
             * This is because this is our very first time being asked to render
             * and we want to reuse the last result, but there isn't one to
             * reuse.
             *
             * The only times that this occurs are when showing modals with
             * server-side rendering, or when showing tests and quizzes in all cases.
             * In those cases, we want to load the parent curation page instead.
             * We know that then, the URL will end with /:kind/:slug so we can
             * leverage that and grab everything before :kind and then cut it down
             * from there
             *
             * The following regex has two parts:
             * 1. ((?:/[^/]*)+)
             * 2. (?:/[^/]*){2}
             *
             * The first part grabs the bit we want. It is a capturing group
             * that looks for one or more segments like "/math".
             *
             * The second part is a non-capturing group that looks for exactly
             * two matching pieces for the kind and slug.
             *
             * Together, this will match any three or more segment URL.
             */
            const rootRegex = new RegExp("((?:/[^/]*)+)(?:/[^/]*){2}");
            const match = rootRegex.exec(pathname);
            if (!match || !match[1]) {
                // If we get here, well, it's all over.
                throw new KAError(
                    "Cannot determine content path to load",
                    Errors.Internal,
                );
            }

            // Now the path could be /domain/course/unit/blah/blah.
            // We want to make sure that the largest list we can have is
            // /domain/course/unit so split, slice, and join.  This also
            // allows /domain/course for course challenges.
            const slugs = match[1].split("/");
            const curationPath = slugs.slice(0, 4).join("/");

            // Finally, we want to delete the `modal` parameter from the extra
            // params.  We are no longer asking for modal content since we're asking
            // for curation page content.  Doing this allows the caching to work
            // when we exit the modal view.
            extraParams.delete("modal");

            return {
                path: decodeURIComponent(curationPath),
                queryParams: Object.fromEntries(extraParams.entries()),
            };
        }

        return {
            path: decodeURIComponent(pathname),
            queryParams: Object.fromEntries(extraParams.entries()),
        };
    };

    const renderRoute = (
        location: Location,
        match: Match,
        reuseLastLocation?: (params?: URLSearchParams) => boolean,
        pathname?: string,
    ): React.ReactNode => {
        const options = _getDataOptions(
            location,
            reuseLastLocation,
            pathname ?? location.pathname,
        );
        return _renderData(pathname ?? location.pathname, match, options);
    };

    /**
     * 1. The content data we get is totally dependent on our current page
     * location, so let's use routing to control it.
     *
     * 2. We also want to avoid requesting data at all for specific scenarios.
     * Instead we rely on the existing data we got. This is a little tricky
     * but not impossible. That is handled in the above render function
     * and the reuse callback that it takes.
     *
     * 3. In addition for some cases, we want to preserve the data we have
     * while we load the data we want. This allows for some nice smooth UX
     * transitions around a tutorial page. That is handled by our use of
     * the MaybePreserveLastResult component.
     */
    return (
        <Switch>
            <Route
                path="/devadmin/content/preview/:parentTopicId/:topicId"
                render={({location, match}) =>
                    renderRoute(location, match, () => false)
                }
            />
            <Route
                path="/:domain/*/:kind(v|a|e)/:slug"
                render={({location, match}) =>
                    renderRoute(location, match, (e) => e?.get("modal") === "1")
                }
            />
            <Route
                path="/:domain/*/:kind(p[a-z]?)/:slug"
                render={({location, match}) => renderRoute(location, match)}
            />
            <Route
                path="/*/:kind(quiz|test)/:slug"
                render={({location, match}) =>
                    renderRoute(location, match, () => true)
                }
            />
            <Route
                path="/:domain/:subject/:kind(d|t)"
                render={({location, match}) => {
                    const {domain, subject} = match.params;
                    if (domain == null || subject == null) {
                        return <NotFound />;
                    }
                    return renderRoute(
                        location,
                        match,
                        () => false,
                        `/${domain}/${subject}`,
                    );
                }}
            />
            <Route
                path="/:domain/:subject?/:unit?"
                render={({location, match}) => renderRoute(location, match)}
            />
            <Route path="*">
                <NotFound />
            </Route>
        </Switch>
    );
};

/**
 * The withContentData HOC is the thing that gets exported.  We don't want to expose
 * the actual ContentDataProvider component and want to encourage the withContentData HOC
 */

export type WithContentDataProps<TData extends ContentData> = {
    contentData: TData | null | undefined;
    contentDataError: string | null | undefined;
    contentDataLoading?: boolean;
};

export type WithoutContentData<TProps, TData extends ContentData> = Omit<
    TProps,
    keyof WithContentDataProps<TData>
>;

// NOTE(kevinb): Note sure why we have a function that returns the HOC since there aren't
// any parameters to the function.  I'm leaving it as is for now, but we should consider
// removing it after the TypeScript migration.
const withContentData =
    () =>
    <
        TData extends ContentData,
        C extends React.ComponentType<
            React.ComponentProps<C> & WithContentDataProps<TData>
        > & {
            displayName?: string | undefined;
        },
        OuterProps = Omit<
            JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
            keyof WithContentDataProps<TData>
        >,
    >(
        WrappedComponent: C,
    ): React.ComponentType<OuterProps> => {
        const ComponentWithContentData = (props: OuterProps) => (
            <ContentDataProvider>
                {(contentDataProps) => {
                    const data =
                        contentDataProps.status === "success"
                            ? contentDataProps.data
                            : null;
                    const error =
                        contentDataProps.status === "error"
                            ? contentDataProps.error
                            : null;
                    return (
                        <WrappedComponent
                            {...(props as any)}
                            contentData={data}
                            contentDataError={error?.message}
                            contentDataLoading={
                                contentDataProps.status === "loading" ||
                                contentDataProps.status === "no-data"
                            }
                        />
                    );
                }}
            </ContentDataProvider>
        );

        // Try to create a nice displayName for React Dev Tools.
        const displayName =
            WrappedComponent.displayName ||
            WrappedComponent.name ||
            "Component";

        ComponentWithContentData.displayName = `withContentData(${displayName})`;

        return ComponentWithContentData;
    };

export default withContentData;
